import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Video, Calendar, Loader2, ExternalLink, ClipboardList } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const EMPTY = {
  course_id: "", class_title: "", description: "",
  class_date: "", start_time: "", end_time: "", timezone: "Asia/Kolkata",
  meeting_platform: "zoom", meeting_link: "", meeting_id: "", passcode: "",
};

export default function LiveClassesPage() {
  const [rows, setRows] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/live-classes");
      setRows(data.data.live_classes);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/faculty/my-courses");
        setCourses(data.data.courses);
      } catch { /* silent */ }
    })();
  }, []);

  const submit = async () => {
    if (!form.course_id || !form.class_title || !form.class_date || !form.start_time || !form.end_time || !form.meeting_link) {
      return toast.error("Fill all required fields.");
    }
    setSaving(true);
    try {
      await api.post("/admin/live-classes", form);
      toast.success("Live class scheduled.");
      setDlg(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/live-classes/${id}/status`, { status });
      toast.success(`Status changed to ${status}.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="Live classes" breadcrumbs={["Faculty", "Live classes"]}>
      <div className="space-y-4" data-testid="live-classes">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between">
          <div className="text-sm text-slate-600">Schedule Zoom / Google Meet / Teams live sessions for your courses.</div>
          <button onClick={() => { setForm(EMPTY); setDlg({ create: true }); }} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="live-class-add">
            <Plus className="h-4 w-4" /> Schedule live class
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading…" /> : rows.length === 0 ? (
            <EmptyState title="No live classes yet" description="Schedule your first live class." icon={Video} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Title</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Course</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">When</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Platform</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`live-class-row-${r.id}`}>
                    <TableCell><div className="font-semibold text-slate-900">{r.class_title}</div></TableCell>
                    <TableCell className="text-xs text-slate-600">{r.course_title || <span className="font-mono">{r.course_id?.slice(0, 8)}…</span>}</TableCell>
                    <TableCell className="text-xs text-slate-600">{r.class_date?.slice(0, 10)} {r.start_time}–{r.end_time}</TableCell>
                    <TableCell className="text-xs text-slate-600 capitalize">{r.meeting_platform}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.meeting_link && <a href={r.meeting_link} target="_blank" rel="noreferrer" className="inline-flex h-7 w-7 items-center justify-center rounded text-[#101876] hover:bg-slate-100"><ExternalLink className="h-3.5 w-3.5" /></a>}
                        {r.status === "scheduled" && <button onClick={() => changeStatus(r.id, "completed")} className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:border-emerald-400">Mark complete</button>}
                        {r.status !== "cancelled" && r.status !== "completed" && <button onClick={() => changeStatus(r.id, "cancelled")} className="rounded border border-slate-200 px-2 py-1 text-xs font-semibold text-rose-600 hover:border-rose-400">Cancel</button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Schedule live class</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Course *</Label>
              <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                <SelectTrigger data-testid="live-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Title *</Label><Input value={form.class_title} onChange={(e) => setForm({ ...form, class_title: e.target.value })} data-testid="live-title" /></div>
            <div className="space-y-1"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Date *</Label><Input type="date" value={form.class_date} onChange={(e) => setForm({ ...form, class_date: e.target.value })} data-testid="live-date" /></div>
              <div className="space-y-1"><Label>Start *</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="live-start" /></div>
              <div className="space-y-1"><Label>End *</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="live-end" /></div>
            </div>
            <div className="space-y-1">
              <Label>Platform</Label>
              <Select value={form.meeting_platform} onValueChange={(v) => setForm({ ...form, meeting_platform: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="google_meet">Google Meet</SelectItem>
                  <SelectItem value="teams">Microsoft Teams</SelectItem>
                  <SelectItem value="custom">Custom link</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Meeting link *</Label><Input value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="https://zoom.us/j/…" data-testid="live-link" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Meeting ID</Label><Input value={form.meeting_id} onChange={(e) => setForm({ ...form, meeting_id: e.target.value })} /></div>
              <div className="space-y-1"><Label>Passcode</Label><Input value={form.passcode} onChange={(e) => setForm({ ...form, passcode: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="live-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Schedule
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
